import gql from "graphql-tag";
import { incidentFields, organizationFields } from "../../graphql/fragments";

export const FACILITY_SUBSCRIPTIONS = gql`
  query FacilitySubscriptions(
    $organizationId: ID!
    $dateTimeFilter: DateTimeFilter
  ) {
    facilitySubscriptions(
      organizationId: $organizationId
      dateTimeFilter: $dateTimeFilter
    ) {
      activeSmartUnits
      activeSubscriptions
      added
      ended
    }
  }
`;

export const INCIDENT_HISTORY = gql`
  query IncidentHistory(
    # Nullable: callers that scope by an explicit facility list send parentIds
    # instead. A single root would be ANDed with that list server-side, so any
    # facility outside it would be filtered away.
    $parentId: ID
    $dateTimeFilter: DateTimeFilter
    $organizationSubtypes: [String]
    $organizationSubtype: String
    $resolutions: [String]
    $statuses: [String]
    $types: [String]
    $type: String
    $duringAccessHours: Boolean
    $bypassedAccess: Boolean
    $assigned: Boolean
    $accessStatuses: [String]
    $searchText: String
    $dispositions: [String]
    $priorities: [String]
    $isOverdue: Boolean
    $limit: Int
    $seqIdGt: Int
    $seqIdLt: Int
    $parentIds: [ID!]
    $organizationIds: [ID!]
  ) {
    incidents(
      parentId: $parentId
      dateTimeFilter: $dateTimeFilter
      organizationSubtypes: $organizationSubtypes
      organizationSubtype: $organizationSubtype
      resolutions: $resolutions
      statuses: $statuses
      types: $types
      duringAccessHours: $duringAccessHours
      assigned: $assigned
      bypassedAccess: $bypassedAccess
      accessStatuses: $accessStatuses
      searchText: $searchText
      sort: "startedAt desc"
      dispositions: $dispositions
      priority: $priorities
      isOverdue: $isOverdue
      limit: $limit
      seqIdGt: $seqIdGt
      seqIdLt: $seqIdLt
      type: $type
      parentIds: $parentIds
      organizationIds: $organizationIds
    ) {
      ...IncidentFields
      seqId
      survey
      organization {
        ...OrganizationFields
        devices {
          type
          subtype
          id
          name
          externalId
          shortId
        }
      }
      tasks {
        id
        taskId
        description
        dueAt
        insertedAt
        priority
        resolution
        title
        updatedAt
        assignee {
          id
          firstName
          lastName
          username
        }
      }
      alerting_strategy {
        phone_number
        time_zone
        alert_templates {
          id
          name
        }
        application_org {
          id
          name
          type
        }
        device_org {
          id
          name
          type
        }
        facility_org {
          id
          name
          type
        }
      }
      zone_profile {
        climateConfig {
          disabled
          highHumidityThreshold
          highTemperatureThreshold
          lowHumidityThreshold
          lowTemperatureThreshold
        }
        entryConfig {
          disabled
          schedule
          startsAt
          endsAt
        }
        motionConfigs {
          disabled
          mode
          schedule
          startsAt
          endsAt
        }
        serviceRequestConfig {
          disabled
          schedule
          startsAt
          endsAt
        }
        videoConfig {
          disabled
          videoUrl
          videoCameraEvents
        }
      }
      alerts {
        id
        external_id
        status
        type
        escalation_level
        to
        from
        body
        template_id
        response_mode
        events {
          id
          data
          name
          timestamp
          type
        }
        responder {
          id
          name
          type
          escalationLevel
          contactMethod {
            id
            type
            value
          }
        }
      }
      devices {
        id
        external_id
        short_id
        name
        organization_id
        triggered
        type
        location
      }
      events {
        id
        data
        name
        timestamp
        type
      }
      responders {
        id
        escalationLevel
        name
        type
        status
        contactMethod {
          id
          type
          value
        }
      }
    }
  }
  ${incidentFields}
  ${organizationFields}
`;

export const FACILITY_REPORT_INCIDENTS = gql`
  query FacilityReportIncidents($parentId: ID!, $after: DateTime!) {
    incidents(
      parentId: $parentId
      dateTimeFilter: { after: $after }
      organizationSubtype: "unit"
      sort: "startedAt asc"
    ) {
      ...IncidentFields
    }
  }
  ${incidentFields}
`;

export const TENANT_LEDGER_REPORT_STATUS = gql`
  query tenantLedgerLogs(
    $organizationId: ID!
    $dateTimeFilter: DateTimeFilter!
  ) {
    tenantLedgerLogs(
      organizationId: $organizationId
      dateTimeFilter: $dateTimeFilter
      sort: "id desc"
    ) {
      id
      nameOfReport
      facilityId
      dateOfReport
      initialEmailSentStatus
      emailResults
      statusOfReport
      organizationId
      pmsName
    }
  }
`;

export const TENANT_LEDGER_PMS_REPORT = gql`
  query tenantLedgerPmsReport(
    $organizationId: ID!
    $dateTimeFilter: DateTimeFilter!
    $pmsName: String
  ) {
    tenantLedgerPmsReport(
      organizationId: $organizationId
      dateTimeFilter: $dateTimeFilter
      pmsName: $pmsName
      sort: "id desc"
    ) {
      corpCode
      facilityId
      insertedAt
      sdChargeAmount
      sdChargeId
      sdChargeReconcilation
      sdEndDate
      sdStartDate
      sdUnitId
      sdUnitName
      siteId
      organizationId
      ledgerId
      pmsFacilityName
      pmsLeaseId
      pmsMoveInBy
      pmsMoveOutBy
      pmsChargeAddedBy
      pmsChargeRemovedBy
      pmsName
      transferBy
      transferFrom
      transferredUnitType
      deviceCount
      tenantFirstName
      tenantLastName
      sdActualChargeAmount
      sdLedgerQuantity
    }
  }
`;

export const ORGANIZATION_PROFILE = gql`
  query Organization($id: ID!) {
    organization(id: $id) {
      id
      profile {
        ... on DefaultProfile {
          email
          extension
          phoneNumber
          autoEmailForScheduler
          enableTenantLedger
          tenantLedgerReportType
          enableRecurringchargeReconciliationReport
          reconciliationReportType
          autoEmailForReconciliationReport
        }
      }
    }
  }
`;

export const SEND_TENANT_LEDGER_EMAIL = gql`
  query sendEmail(
    $organizationId: ID!
    $nowUtc: DateTime
    $email: String!
    $pmsName: String
  ) {
    sendEmail(
      organizationId: $organizationId
      nowUtc: $nowUtc
      email: $email
      pmsName: $pmsName
    ) {
      status
      message
    }
  }
`;

export const RECONCILIATION_REPORT = gql`
  query ReconciliationReport(
    $organizationId: ID!
    $dateTimeFilter: DateTimeFilter!
    $facilityIds: [ID]!
    $chargeStatus: String
  ) {
    reconciliationReport(
      organizationId: $organizationId
      dateTimeFilter: $dateTimeFilter
      sort: "id desc"
      facilityIds: $facilityIds
      chargeStatus: $chargeStatus
    ) {
      id
      organizationId
      facilityId
      facilityName
      sdStartDate
      sdUnitName
      sdEndDate
      sdRecurringChargeName
      sdChargeReconcilation
      sdDeviceAssigned
      sdChargeAmount
    }
  }
`;

export const SEND_RECURRING_CHARGE_REPORT_EMAIL = gql`
  mutation sendRecurringChargeEmail(
    $organizationId: ID!
    $dateTimeFilter: DateTimeFilter!
    $email: String!
    $facilityIds: [ID]!
    $chargeStatus: String
  ) {
    sendRecurringChargeEmail(
      organizationId: $organizationId
      dateTimeFilter: $dateTimeFilter
      email: $email
      facilityIds: $facilityIds
      chargeStatus: $chargeStatus
    ) {
      status
      message
    }
  }
`;

export const STOREDGE_SUBSCRIPTIONS_BY_USER = gql`
  query StoredgeSubscriptionsByUser(
    $organizationId: ID!
    $fromDate: Date!
    $toDate: Date!
    $invokeFrom: String
  ) {
    storedgeSubscriptionsByUser(
      organizationId: $organizationId
      fromDate: $fromDate
      toDate: $toDate
      invokeFrom: $invokeFrom
    ) {
      month
      companyName
      userName
      firstName
      lastName
      smartUnitSubscriptionsAddInMonth
      transferredToSmartUnit
      totalSubscriptionsByUser
    }
  }
`;
